import { useState, useEffect, useRef } from "react";
import { NavigateOptions, To, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import ReactEcharts from "echarts-for-react";
import { EChartsOption } from "echarts-for-react";
import { skipToken } from "@reduxjs/toolkit/query";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import {
	ArrowDownIcon,
	ArrowRightIcon,
	EnvelopeIcon,
	EyeIcon,
	EyeSlashIcon,
	LockClosedIcon,
	LockOpenIcon,
	MagnifyingGlassIcon,
	MinusIcon,
	PencilIcon,
	PlusIcon,
	PrinterIcon,
	UsersIcon,
} from "@heroicons/react/24/solid";

import { draftTitle } from "../../app/draftHelper";
import { countWords, getParamAsInt, scrollToTop, toOrdinal } from "../../app/utils";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { rtkqErrorToString } from "../../app/rtkQueryHelper";
import { clearSharedAlert, setSharedAlert } from "../../app/siteSlice";
import { scrollToSection, useScrollToSection } from "../../app/useScrollToSection";
import { UserRole } from "../../app/userSlice";
import Avatar from "../../components/Avatar";
import ModalAlert from "../../components/ModalAlert";
import StackedDate from "../../components/StackedDate";
import ButtonWithConfirm from "../../components/ButtonWithConfirm";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import SupportLink from "../../components/SupportLink";
import NamedEntity from "../../interfaces/NamedEntity";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import { isContrastMode } from "../../app/contrastSlice";
import Messages from "../messages/Messages";
import {
	PeerReviewViewModel,
	usePeerReviewMarkAsReadMutation,
} from "../peerReviews/peerReviewsApiSlice";
import { PeerReviewInteraction } from "../peerReviews/PeerReviewInteraction";
import PromptPanel from "../prompts/PromptPanel";
import { DraftStatus } from "../writing/writingApiSlice";
import {
	useDeleteDraftMutation,
	useReviseDraftMutation,
	useUndoFinalizeDraftMutation,
} from "../writing/drafting/draftingApiSlice";
import { miwChartColors, utahChartColors } from "../reports/shared/reportUtil";
import CommentedEssay from "../writing/drafting/CommentedEssay";
import Organizer from "../writing/organizing/Organizer";
import WritingAnalysis from "./WritingAnalysis";
import ScoreReportChart from "./ScoreReportChart";
import PlagiarismAnalysis from "./PlagiarismAnalysis";
import {
	DraftScoreNormViewModel,
	DraftScoreViewModel,
	DraftViewModel,
	ScoreReportViewModel,
	ScoredCategoryViewModel,
	WritingAnalysisViewModel,
	useScoreReportByEssayQuery,
	useScoreReportQuery,
	useSendReviewRequestMutation,
} from "./scoreReportApiSlice";

export default function ScoreReport() {
	const [queryParams] = useSearchParams();
	const draftIdParam = getParamAsInt(queryParams, "draftId");
	const essayIdParam = getParamAsInt(queryParams, "essayId");

	const [view, setView] = useState<string>("legacy");
	const [draftId, setDraftId] = useState<number>(0);
	const [showOrganizer, setShowOrganizer] = useState(false);
	const [showAll, setShowAll] = useState(true);
	const [scoreReport, setScoreReport] = useState<ScoreReportViewModel | undefined>();
	const [wordCount, setWordCount] = useState(0);
	const [analysis, setAnalysis] = useState<WritingAnalysisViewModel | undefined>();
	const [lastErrorMessage, setLastErrorMessage] = useState<string>();
	const [fetchError, setFetchError] = useState<string | undefined>();
	const navigating = useRef(false);

	const location = useLocation();
	const navigate = useNavigate();

	const dispatch = useAppDispatch();
	const sharedAlert = useAppSelector((state) => state.site.sharedAlert);
	const config = useAppSelector((state) => state.site.config);
	const role = useAppSelector((state) => state.user.role);

	const refPrintAreaOne = useRef<HTMLDivElement>(null);
	const refPrintAreaTwo = useRef<HTMLDivElement>(null);

	const byDraftResult = useScoreReportQuery(draftIdParam ?? skipToken);
	const byEssayResult = useScoreReportByEssayQuery(essayIdParam ?? skipToken);

	const [callReviseDraft] = useReviseDraftMutation();
	const [callDeleteDraft] = useDeleteDraftMutation();
	const [callUndoFinalizeDraft] = useUndoFinalizeDraftMutation();

	useScrollToSection();

	useEffect(() => {
		if (sharedAlert && !navigating) {
			setLastErrorMessage(sharedAlert.message);
			dispatch(clearSharedAlert());
		}
	}, [sharedAlert, navigating]);

	useEffect(() => {
		const data = byDraftResult.data ?? byEssayResult.data;
		if (!data) return;

		setDraftId(data.draft.id);
		setScoreReport(data);
		setWordCount(countWords(data.pureTextForWordCount));
		setShowAll(data.drafts.length > 5 ? false : true);

		if (
			location.state != null &&
			(location.state.section === "EssayMessages" ||
				location.state.section === "OrganizerSection") &&
			!data.draftScore &&
			data.organizer &&
			data.organizer.htmlTemplate
		) {
			setShowOrganizer(true);
		}

		setAnalysis({
			id: data.draftScore?.id,
			showScore: data.draftScore !== null,
			genre: data.prompt.genre.name,
			categories: data.draftScore?.categoryScores,
			cSharpScoreFormat: data.draftScore?.cSharpScoreFormat,
			decimalPlacesDisplayed: data.draftScore?.decimalPlacesDisplayed,
			canScoreProctorScorableCategories: data.canScoreProctorScorableCategories,
			staticFeedback: data.staticFeedback,
			targetedFeedback: data.targetedFeedback,
			llmFeedback: data.llmFeedback,
			feedbackTutorials: data.feedbackTutorials,
		});
	}, [byDraftResult.data, byEssayResult.data]);

	useEffect(() => {
		if (!byDraftResult.isError && !byEssayResult.isError) return;

		let errorMsg = "";
		if (byDraftResult.isError) {
			errorMsg = rtkqErrorToString(byDraftResult.error);
		}
		if (byEssayResult.isError) {
			errorMsg = rtkqErrorToString(byEssayResult.error);
		}

		setFetchError(errorMsg);
	}, [byDraftResult.isError, byEssayResult.isError]);

	function doNavigate(to: To, options?: NavigateOptions) {
		navigating.current = true;
		navigate(to, options);
	}

	function handleProctorScore(categoryScore: ScoredCategoryViewModel) {
		if (!analysis) return;
		const categoryScores = analysis.categories.map((obj) => {
			if (obj.id === categoryScore.id) {
				return categoryScore;
			}
			return obj;
		});
		const _analysis = { ...analysis, categories: categoryScores };
		setAnalysis(_analysis);
	}

	function handlePrintScoreReport() {
		window.print();
	}

	function handlePrintEssay() {
		const mywindow = window.open("", "PRINT", "height=400,width=600");
		if (!mywindow) return false;
		if (!refPrintAreaOne || !refPrintAreaOne.current) return false;
		if (!refPrintAreaTwo || !refPrintAreaTwo.current) return false;

		const head = document.head.innerHTML
			.replace(/<title>.+<\/title>/, "")
			.replace(/<script type="module">[\s\S]+<\/script>/m, "")
			.replace(/<script type="module" src="[^"]+">[\s\S]+<\/script>/m, "");

		const title =
			draftTitle(scoreReport?.commentedEssay.title).trim().length > 0
				? draftTitle(scoreReport?.commentedEssay.title)
				: (scoreReport?.prompt.title ?? "No Title");
		const docTitle = `${scoreReport?.writerName} - ${title}`;

		const doc = `
<html>
<head>
  <title>${docTitle}</title>
  ${head}
  <style>
    #areaTwo .toolbar { display: none; }
  </style>
</head>
<body class="m-8">
  <div id="areaOne" class="mt-8">${refPrintAreaOne.current.innerHTML}</div>
  <div id="areaTwo" class="mt-8">${refPrintAreaTwo.current.innerHTML}</div>
</body>
</html>`;

		mywindow.document.write(doc);
		mywindow.document.close(); // necessary for IE >= 10
		mywindow.focus(); // necessary for IE >= 10*/
		mywindow.print();
		mywindow.close();

		return true;
	}

	function handleContinueEssay(e: React.MouseEvent<HTMLButtonElement>) {
		e.preventDefault();

		if (!draftId) return;
		doNavigate(`/writing/write?draftId=${draftId}`);
	}

	function handleReviseEssay() {
		if (!draftId) return;
		callReviseDraft(draftId)
			.unwrap()
			.then((payload) => {
				doNavigate(`/writing/write?draftId=${payload.draftId}`);
			})
			.catch((reason) => {
				console.log(`reviseDraft failed: ${JSON.stringify(reason)}`);
			});
	}

	function handleFinalizeEssay() {
		if (!draftId) return;
		doNavigate(`/writing/preview?draftId=${draftId}`);
	}

	function handleUndoFinalizeEssay() {
		if (!draftId) return;
		callUndoFinalizeDraft(draftId)
			.unwrap()
			.catch((reason) => {
				console.log(`undoFinalizeDraft failed: ${JSON.stringify(reason)}`);
			});
	}

	function handlePreviewEssay() {
		if (!draftId) return;
		doNavigate(`/writing/preview?draftId=${draftId}`);
	}

	function handleDeleteDraft() {
		if (!draftId) return;
		callDeleteDraft(draftId)
			.unwrap()
			.then(() => {
				const draft = scoreReport?.drafts.findLast((d) => d.id != draftId);
				if (draft) {
					doNavigate(`/scoreReport?draftId=${draft.id}`);
				} else {
					doNavigate(`/home`);
					dispatch(
						setSharedAlert({
							level: AlertLevel.Info,
							message: "An essay was deleted when its last draft was deleted.",
						})
					);
				}
			})
			.catch((reason) => {
				console.log(`deleteDraft failed: ${JSON.stringify(reason)}`);
			});
	}

	function handleChangeDraft(draftId: number) {
		if (!scoreReport) return;

		const draft = scoreReport.drafts.find((d) => d.id === draftId);
		if (!draft) return;

		if (draft.status === DraftStatus.Incomplete) {
			if (!scoreReport.dueDateExceeded || !scoreReport.dueDateRequired) {
				doNavigate(`/writing/write?draftId=${draft.id}`);
			}
		} else if (draft.hasLegacyHtmlFormat) {
			doNavigate(`/writing/preview?draftId=${draft.id}`);
		}
		doNavigate(`/scoreReport?draftId=${draft.id}`);
	}

	const [requestReview] = useSendReviewRequestMutation();

	function confirmRequestReview() {
		if (draftId) {
			requestReview(draftId);
		}
	}

	function handleClearError() {
		setLastErrorMessage(undefined);
	}

	if (draftIdParam === undefined && essayIdParam === undefined) return null;

	return (
		<main>
			<div className="flex justify-between">
				<div>
					<h1>Score Report</h1>
				</div>
				<div className="flex flex-col items-end gap-2 print:hidden">
					<SelectDevelopmentView
						value={view}
						setValue={(v) => setView(v as string)}
						simpleListBoxId="viewFilter"
						options={[
							{ value: "legacy", label: "Legacy" },
							{ value: "raw", label: "Raw Data" },
						]}
						fieldClasses="w-64"
					/>
					{view === "legacy" && scoreReport && analysis && wordCount > 0 && (
						<button
							id="print-report-button"
							className="btn btn-neutral ml-4"
							onClick={handlePrintScoreReport}>
							<PrinterIcon className="h-4 inline mr-2" />
							Print Report
						</button>
					)}
				</div>
			</div>
			{fetchError && (
				<ModalAlert
					open={true}
					title={"Error"}
					message={fetchError}
					type={AlertLevel.Error}
					confirmLabel={"OK"}
					onConfirm={() => doNavigate(`/home`)}
				/>
			)}
			{view === "legacy" && scoreReport && analysis && (
				<>
					<section id="PromptSection" ref={refPrintAreaOne}>
						<div className="flex">
							<StackedDate
								className="ml-8 mr-24"
								date={scoreReport.draft.createDate}
							/>
							<div>
								<h2>{scoreReport.prompt.title}</h2>
								<div>by {scoreReport.writerName}</div>
								<div>for {scoreReport.course.name}</div>
							</div>
						</div>
						<div>
							<PromptPanel
								name={scoreReport.prompt.title}
								showName={false}
								renderedText={scoreReport.prompt.renderedText}
								stimulusMaterials={scoreReport.stimulusMaterials}
								showGenre={false}></PromptPanel>
						</div>
					</section>
					<AlertMessage
						className="mt-8"
						type={AlertLevel.Error}
						message={lastErrorMessage}
						showIcon={true}
						onClear={handleClearError}
					/>
					<div className="relative mt-8">
						{scoreReport.organizer && scoreReport.organizer.htmlTemplate && (
							<section id="OrganizerSection" className="flex z-40">
								<div className="w-3/4">
									{showOrganizer && (
										<Organizer
											className="py-4 relative"
											html={scoreReport.organizer.htmlTemplate}
											response={scoreReport.organizer.organizerResponse}
											readonly={true}
										/>
									)}
								</div>
								<div className="w-1/4 ml-8 mt-3 absolute -right-2">
									<button
										id="show-organizer-button"
										className="btn btn-neutral text-left print:!hidden"
										onClick={() => setShowOrganizer(!showOrganizer)}>
										{showOrganizer ? (
											<span>
												<EyeSlashIcon className="h-4 inline mr-2" />
												Hide Organizer
											</span>
										) : (
											<span>
												<EyeIcon className="h-4 inline mr-2" />
												Show Organizer
											</span>
										)}
									</button>
								</div>
							</section>
						)}
						<section id="DraftArea" className="flex">
							<div className="w-3/4" ref={refPrintAreaTwo}>
								<CommentedEssay
									commentedEssay={scoreReport.commentedEssay}
									isScoreReport={true}
								/>
							</div>
							<div className="w-1/4 ml-8 mt-4">
								{scoreReport.draftScore && (
									<div>
										<h2 className="mt-12 relative z-1">Total Score</h2>
										<div
											className="relative -top-8 -left-8"
											id="total-score-graphic">
											<TotalScoreGraphic
												totalScore={scoreReport.draftScore.totalScore}
												maxScore={
													scoreReport.prompt.genre.name ==
													"Constructed Response"
														? 3
														: 30
												}></TotalScoreGraphic>
										</div>
										<button
											id="view-scores-button"
											className="btn btn-neutral print:hidden relative -top-40 my-4"
											onClick={() => scrollToSection("DraftScores")}>
											<ArrowDownIcon className="h-4 inline mr-2" />
											View Scores
										</button>
									</div>
								)}

								<div
									className={`${scoreReport.draftScore ? "-mt-44" : ""} relative z-1`}>
									<div
										className={`${scoreReport.draftScore ? "mt-8" : "mt-12"} text-xl font-medium`}>
										Word Count:&nbsp;
										<span id="word-count" className="font-normal">
											{wordCount}
										</span>
									</div>

									<div className="mb-6">
										<h2 className="mt-6">Submissions</h2>

										<div className="pb-2">
											{scoreReport.remainingRevisions} submissions left
										</div>
										<DraftList
											drafts={scoreReport.drafts}
											currentDraft={scoreReport.draft.draftNumber}
											status={scoreReport.draft.status}
											showAll={showAll}
											draftsWithReviews={scoreReport.reviewedTestIds}
											hasLegacyHtmlFormat={
												scoreReport.draft.hasLegacyHtmlFormat
											}
											onChangeDraft={handleChangeDraft}
										/>
										{scoreReport.drafts.length > 5 && (
											<div
												className="mt-1 text-black cursor-pointer"
												onClick={() => setShowAll((v) => !v)}>
												{showAll ? (
													<span className="relative flex items-center font-medium">
														<MinusIcon className="h-4 inline mr-0" />
														&nbsp;Show Less
													</span>
												) : (
													<span className="relative flex items-center font-medium">
														<PlusIcon className="h-4 inline mr-0 " />
														&nbsp;Show All
													</span>
												)}
											</div>
										)}
									</div>

									{scoreReport.draft.status === DraftStatus.Incomplete &&
										scoreReport.isWriter && (
											<button
												id="continue-essay-button"
												className="btn btn-neutral mt-4 !block print:!hidden"
												onClick={handleContinueEssay}
												disabled={
													scoreReport.dueDateExceeded &&
													scoreReport.dueDateRequired
												}>
												<PencilIcon className="h-4 inline mr-2" />
												Continue Writing
											</button>
										)}

									{(scoreReport.draft.status === DraftStatus.Scored ||
										scoreReport.draft.status === DraftStatus.ScoringFailed) &&
										scoreReport.isWriter && (
											<>
												<ButtonWithConfirm
													id="revise-button"
													className="btn btn-primary mt-4 !block print:!hidden"
													disabled={
														(scoreReport.dueDateExceeded &&
															scoreReport.dueDateRequired) ||
														scoreReport.remainingRevisions === 0
													}
													message="Are you sure you want to revise this essay?"
													type={AlertLevel.Info}
													confirmLabel="Yes"
													cancelLabel="No"
													onConfirm={handleReviseEssay}>
													<PencilIcon className="h-4 inline mr-2" />
													Revise Essay
												</ButtonWithConfirm>
											</>
										)}

									{scoreReport.draft.status === DraftStatus.Scored &&
										scoreReport.canUserFinalize && (
											<button
												id="finalize-button"
												className="btn btn-alert mt-4 !block print:!hidden"
												onClick={handleFinalizeEssay}>
												<LockClosedIcon className="h-4 inline mr-2" />
												Finalize Essay
											</button>
										)}

									{scoreReport.canUserPreview &&
										!scoreReport.canUserFinalize &&
										scoreReport.draft.status !== DraftStatus.Deleted &&
										wordCount > 0 && (
											<button
												id="preview-button"
												className="btn btn-neutral mt-4 !block print:!hidden"
												onClick={handlePreviewEssay}>
												<EyeIcon className="h-4 inline mr-2" />
												Preview Draft
											</button>
										)}

									{wordCount > 0 && (
										<button
											id="print-draft-button"
											className="btn btn-neutral mt-4 !block print:!hidden"
											onClick={handlePrintEssay}>
											<PrinterIcon className="h-4 inline mr-2" />
											Print Draft
										</button>
									)}

									{scoreReport.draft.status === DraftStatus.Finalized &&
										scoreReport.canUserRetract && (
											<ButtonWithConfirm
												id="undo-finalize-button"
												className="btn btn-alert mt-4 !block print:!hidden"
												message="You are about to retract a student’s finalized essay so they may continue writing. Are you sure you want to undo finalization?"
												type={AlertLevel.Warning}
												confirmLabel="Yes"
												cancelLabel="No"
												onConfirm={handleUndoFinalizeEssay}>
												<LockOpenIcon className="h-4 inline mr-2" />
												Undo Finalize
											</ButtonWithConfirm>
										)}

									{scoreReport.canDeleteDraft && (
										<ButtonWithConfirm
											id="delete-button"
											className="btn btn-danger mt-4 !block print:!hidden"
											message="Are you sure you want to delete this draft?"
											type={AlertLevel.Error}
											confirmLabel="Yes"
											cancelLabel="No"
											onConfirm={handleDeleteDraft}>
											<span className="h-4 inline mr-3">✘</span>
											Delete Draft
										</ButtonWithConfirm>
									)}

									{scoreReport.showPeerReviewSection && (
										<>
											<h2 className="mt-8">Peer Review</h2>
											{scoreReport.totalRequestedReviewCount == 0 &&
												scoreReport.isWriter && (
													<ButtonWithConfirm
														id="request-reviews-button"
														className="btn btn-neutral mt-4 !block print:!hidden"
														disabled={
															(scoreReport.dueDateExceeded &&
																scoreReport.dueDateRequired) ||
															scoreReport.remainingRevisions === 0
														}
														message="Are you sure you want to request peer review for your essay?"
														type={AlertLevel.Warning}
														confirmLabel="Yes"
														cancelLabel="Cancel"
														onConfirm={confirmRequestReview}>
														<EnvelopeIcon className="h-4 inline mr-2" />
														Request Review
													</ButtonWithConfirm>
												)}
											{scoreReport.totalRequestedReviewCount == 0 &&
												!scoreReport.isWriter && <p>Not requested</p>}
											{scoreReport.totalRequestedReviewCount > 0 && (
												<>
													<p>Sent {scoreReport.dateSent}</p>
													<p>
														{scoreReport.reviewCount} of{" "}
														{scoreReport.totalRequestedReviewCount}{" "}
														complete
													</p>
												</>
											)}
										</>
									)}
								</div>
							</div>
						</section>
					</div>
					{scoreReport.showPeerReviewSection &&
						scoreReport.completedReviews.length > 0 && (
							<>
								<hr className="my-4" />
								<PeerReviews
									draftId={scoreReport.draft.id}
									reviews={scoreReport.completedReviews}
								/>
							</>
						)}
					<hr className="my-4" />
					<section id="DraftScores">
						<ScoreReportScores
							analysis={analysis}
							draftScore={scoreReport.draftScore}
							showDraftNorm={scoreReport.showNormativeScores}
							draftNorm={scoreReport.draftScoreNorm}
							normSupportLinkSuffix={
								(role == UserRole.Writer
									? config?.writerNormsSupportUrlSuffix
									: config?.proctorNormsSupportUrlSuffix) ?? ""
							}
							genre={scoreReport.prompt.genre}></ScoreReportScores>
					</section>
					<hr className="my-4" />
					<section id="WritingAnalysis">
						<WritingAnalysis analysis={analysis} onProctorScore={handleProctorScore} />
					</section>
					<hr className="my-4" />
					{scoreReport.plagiarismAnalysis != null && (
						<>
							<section id="PlagiarismAnalysis">
								<PlagiarismAnalysis analysis={scoreReport.plagiarismAnalysis} />
							</section>
							<hr className="my-4" />
						</>
					)}
					<section id="EssayMessages">
						<Messages draftId={draftId} />
					</section>
					<div className="mt-4 print:hidden">
						[
						<a className="cursor-pointer" onClick={scrollToTop}>
							Return to top
						</a>
						]
					</div>
				</>
			)}
			{view == "raw" && (
				<>
					<RtkqResultViewer heading="Score Report Data By Draft" result={byDraftResult} />
					<RtkqResultViewer heading="Score Report Data By Essay" result={byEssayResult} />
				</>
			)}
		</main>
	);
}

interface DraftListProps {
	drafts: DraftViewModel[];
	currentDraft: number;
	status: DraftStatus;
	draftsWithReviews: number[];
	hasLegacyHtmlFormat: boolean;
	showAll: boolean;
	onChangeDraft: (draftId: number) => void;
}

export function DraftList(props: DraftListProps) {
	function draftSort(a: DraftViewModel, b: DraftViewModel) {
		return b.draftNumber - a.draftNumber;
	}

	function draftLabel(index: number, draft: DraftViewModel) {
		if (index === 0) {
			switch (draft.status) {
				case DraftStatus.Finalized:
					return "Finalized Essay";
				case DraftStatus.Incomplete:
					return "Unsubmitted Draft";
				case DraftStatus.ScoringFailed:
					return "Unscored Draft";
			}

			return draft.hasLegacyHtmlFormat ? "Formatted Essay" : "Latest Draft";
		}
		return `${toOrdinal(draft.draftNumber)} Draft`;
	}

	function draftIcons(draft: DraftViewModel) {
		return (
			<>
				{props.draftsWithReviews?.includes(draft.id) && (
					<>
						<UsersIcon
							className="h-4 inline ml-2 mb-0.5 text-primary-600 outline-none"
							data-tooltip-id="review-tooltip"
						/>
						<Tooltip
							id="review-tooltip"
							place="bottom-start"
							content="Completed Peer Review"></Tooltip>
					</>
				)}
				{draft.hasPlagiarismChecking && (
					<>
						<MagnifyingGlassIcon
							className="h-4 inline ml-2 mb-0.5 text-primary-600 outline-none"
							data-tooltip-id="plagiarism-tooltip"
						/>
						<Tooltip
							id="plagiarism-tooltip"
							place="bottom-start"
							content="Plagiarism Checked"></Tooltip>
					</>
				)}
			</>
		);
	}

	function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
		const draftId = e.currentTarget.dataset.id;
		props.onChangeDraft(Number(draftId));
	}

	const sorted = props.showAll
		? [...props.drafts].sort(draftSort)
		: [...props.drafts].sort(draftSort).slice(0, 5);

	return (
		<ul>
			{sorted.map((d, index) => {
				const label = draftLabel(index, d);
				if (d.draftNumber == props.currentDraft) {
					return (
						<li key={d.id} id={`draft-${d.id}`} className="font-semibold">
							<ArrowRightIcon className="h-6 inline mr-1" />
							{label}
							{draftIcons(d)}
						</li>
					);
				} else {
					return (
						<li key={d.id} id={`draft-${d.id}`}>
							<a data-id={d.id} onClick={handleClick} className="cursor-pointer">
								{label}
								{draftIcons(d)}
							</a>
						</li>
					);
				}
			})}
		</ul>
	);
}

export interface ScoresProps {
	analysis: WritingAnalysisViewModel;
	draftScore: DraftScoreViewModel;
	showDraftNorm: boolean;
	draftNorm: DraftScoreNormViewModel;
	normSupportLinkSuffix: string;
	genre: NamedEntity;
	className?: string;
}

export function ScoreReportScores(props: ScoresProps) {
	if (!props.draftScore) {
		return (
			<>
				<h1>Scores</h1>
				<p>This draft has not been scored.</p>
			</>
		);
	}

	return (
		<section className={props.className}>
			<h1>Scores</h1>
			<div className="flex mt-4">
				<div className="w-1/4">
					<p>
						This essay’s total score is{" "}
						<span className="text-xl ml-2 font-semibold text-primary-600">
							{props.draftScore.totalScore}
						</span>
					</p>
					{props.showDraftNorm && (
						<div>
							<p className="mt-4">
								Percentile Rank is{" "}
								<span className="text-lg bold ml-2">
									{props.draftNorm.percentileRank}
								</span>
							</p>
							<p>
								Stanine is{" "}
								<span className="text-lg bold ml-2">{props.draftNorm.stanine}</span>
							</p>
							<p className="mt-4">
								<SupportLink
									id="percentile-stanine-link"
									article={props.normSupportLinkSuffix}>
									Click here
								</SupportLink>{" "}
								to learn more about percentile ranks and stanines.
							</p>
						</div>
					)}
					<p className="mt-4">
						The essay was scored by PEG, the automated essay scorer, according to the
						scoring standard for your level.
					</p>
				</div>
				<div className="w-3/4 h-full mx-8">
					<ScoreReportChart
						analysis={props.analysis}
						draftScore={props.draftScore}
						isConstructedResponse={props.genre.id === 5 ? true : false}
					/>
				</div>
			</div>
		</section>
	);
}

interface PeerReviewsProps {
	draftId: number;
	reviews: PeerReviewViewModel[];
	expandAllByDefault?: boolean;
}

export function PeerReviews(props: PeerReviewsProps) {
	return (
		<section>
			<h1>Peer Reviews</h1>
			{props.reviews.map((review) => {
				return (
					<PeerReviewAccordion
						key={review.id}
						draftId={props.draftId}
						review={review}
						expandByDefault={props.expandAllByDefault ?? false}
					/>
				);
			})}
		</section>
	);
}

interface PeerReviewAccordionProps {
	draftId: number;
	review: PeerReviewViewModel;
	expandByDefault: boolean;
}

function PeerReviewAccordion(props: PeerReviewAccordionProps) {
	const [isViewed, setIsViewed] = useState(false);
	const [callMarkReviewAsRead] = usePeerReviewMarkAsReadMutation();

	function handleClick(review: PeerReviewViewModel, open: boolean) {
		if (!open && !isViewed) {
			callMarkReviewAsRead({ id: review.id });
			setIsViewed(true);
		}
	}

	return (
		<Disclosure
			as="div"
			key={props.review.id}
			className="pt-6"
			defaultOpen={props.expandByDefault}>
			{({ open }) => (
				<>
					<dt>
						<DisclosureButton
							className={`flex w-full items-start justify-between text-left text-gray-900 p-4 border border-gray-200 bg-gray-50 rounded-md
								${open ? "border-b-0 rounded-b-none" : ""}`}
							onClick={() => handleClick(props.review, open)}>
							<PeerReviewAccordionHeader review={props.review} />
							<span className="ml-6 flex h-7 items-center">
								{open ? (
									<MinusIcon className="h-5 w-5" aria-hidden="true" />
								) : (
									<PlusIcon className="h-5 w-5" aria-hidden="true" />
								)}
							</span>
						</DisclosureButton>
					</dt>

					<PeerReviewAccordionBody
						className="border border-gray-200 border-t-0 rounded-b-md"
						open={open}
						draftId={props.draftId}
						review={props.review}
					/>
				</>
			)}
		</Disclosure>
	);
}

interface PeerReviewHeaderProps {
	review: PeerReviewViewModel;
}

function PeerReviewAccordionHeader({ review }: PeerReviewHeaderProps) {
	return (
		<div className="flex space-x-4 items-center">
			<div>by {review.reviewer}</div>
			<Avatar
				userId={review.reviewerId}
				initials={review.reviewerInitials ?? "?"}
				hasImage={review.reviewerHasImage}
				mini={true}
			/>
		</div>
	);
}

interface PeerReviewBodyProps {
	className?: string;
	open: boolean;
	draftId: number;
	review: PeerReviewViewModel;
}

function PeerReviewAccordionBody(props: PeerReviewBodyProps) {
	const role = useAppSelector((state) => state.user.role);
	return (
		<DisclosurePanel as="dd" className={`${props.className ?? ""} pr-12 p-4`}>
			<PeerReviewInteraction
				draftId={props.draftId}
				review={props.review}
				readOnly={role === UserRole.Proctor}
			/>
		</DisclosurePanel>
	);
}

interface GraphicProps {
	totalScore: number;
	maxScore: number;
}

function TotalScoreGraphic(props: GraphicProps) {
	const echartsRef = useRef<any>(null);
	const { config } = useAppSelector((state) => state.site);
	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;

	let { backgroundColor, foregroundColor, chartPalette } = useAppSelector(
		(state) => state.contrast
	);

	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	} else {
		foregroundColor = "#999";
	}

	const fontFamily = "Montserrat";

	const option: EChartsOption = {
		series: [
			{
				type: "gauge",
				startAngle: 180,
				endAngle: 60,
				min: 0,
				max: props.maxScore,
				splitNumber: props.maxScore == 30 ? 6 : 3,
				itemStyle: {
					color:
						foregroundColor == "#ffff00"
							? foregroundColor
							: chartPalette
								? chartColors[0]
								: chartColors[1],
					shadowColor: foregroundColor,
					shadowBlur: 10,
					shadowOffsetX: 2,
					shadowOffsetY: 2,
				},
				progress: {
					show: true,
					roundCap: true,
					width: 15,
				},
				pointer: {
					show: false,
				},
				axisLine: {
					roundCap: true,
					lineStyle: {
						width: 15,
					},
				},
				axisTick: {
					distance: 2,
					splitNumber: 5,
					lineStyle: {
						width: 1,
						color: foregroundColor,
					},
				},
				splitLine: {
					distance: 2,
					length: 10,
					lineStyle: {
						width: 2,
						color: foregroundColor,
					},
				},
				axisLabel: {
					distance: 25,
					color: foregroundColor,
					fontSize: 10,
				},
				detail: {
					width: "20%",
					offsetCenter: ["15%", "10%"],
					height: 30,
					valueAnimation: true,
					formatter: function (value: number) {
						return "{value|" + value + "}{max|/ " + props.maxScore + "}";
					},
					rich: {
						value: {
							fontSize: 40,
							fontFamily: fontFamily,
							fontWeight: "bold",
							color:
								foregroundColor == "#ffff00"
									? foregroundColor
									: chartPalette
										? chartColors[0]
										: chartColors[1],
						},
						max: {
							fontSize: 18,
							fontFamily: fontFamily,
							color: foregroundColor,
							padding: [0, 0, -20, 10],
						},
					},
				},
				data: [
					{
						value: props.totalScore,
					},
				],
			},
		],
	};

	useEffect(() => {
		const chart = echartsRef.current?.getEchartsInstance();
		if (!chart) return;
		// if not in contrast mode or we are using white on black, dont change chart colors on print.
		if (!isContrastMode() || backgroundColor === "#000000") return;

		const handleBeforePrint: EChartsOption = () => {
			chart.setOption({
				series: [
					{
						itemStyle: {
							color: "#000",
							shadowColor: "transparent",
						},
						axisTick: {
							lineStyle: {
								color: "#000",
							},
						},
						splitLine: {
							lineStyle: {
								color: "#000",
							},
						},
						axisLabel: {
							color: "#000",
						},
						detail: {
							rich: {
								value: {
									color: "#000",
								},
								max: {
									color: "#000",
								},
							},
						},
					},
				],
			});
		};

		const handleAfterPrint = () => {
			chart.setOption({
				series: [
					{
						itemStyle: {
							color:
								foregroundColor == "#ffff00"
									? foregroundColor
									: chartPalette
										? chartColors[0]
										: chartColors[1],
							shadowColor: foregroundColor,
						},
						axisTick: {
							lineStyle: {
								color: foregroundColor,
							},
						},
						splitLine: {
							lineStyle: {
								color: foregroundColor,
							},
						},
						axisLabel: {
							color: foregroundColor,
						},
						detail: {
							rich: {
								value: {
									color:
										foregroundColor == "#ffff00"
											? foregroundColor
											: chartPalette
												? chartColors[0]
												: chartColors[1],
								},
								max: {
									color: foregroundColor,
								},
							},
						},
					},
				],
			});
		};

		window.addEventListener("beforeprint", handleBeforePrint);
		window.addEventListener("afterprint", handleAfterPrint);

		return () => {
			window.removeEventListener("beforeprint", handleBeforePrint);
			window.removeEventListener("afterprint", handleAfterPrint);
		};
	}, [backgroundColor]);

	return <ReactEcharts ref={echartsRef} option={option} className="w-72 !h-80" />;
}
